import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Crown, Info, Laptop, MoreVertical, ShieldCheck, Smartphone } from 'lucide-react';
import {
  PLAN_DEVICE_LIMIT,
  PRIMARY_NOTE,
  SAMPLE_DEVICES,
  cloneDevices,
  isDeviceSort,
  planText,
  removeDevice,
  removeMessage,
  safeDeviceLimit,
  setPrimaryDevice,
  sortDevices,
  usageHint,
  usagePercent,
  usageText,
  type AccountDevice,
  type DeviceSort,
} from '../../data/devices-mock';
import { DevicesArt } from './DevicesArt';
import './devices.css';

const PREVIEW_MS = 680;

export interface DevicesViewProps {
  /**
   * Leave unset to preview skeletons once.
   * Pass a real flag once the devices API is wired.
   */
  loading?: boolean;
  /**
   * Leave unset to use the built-in sample.
   * Pass an array from the API. An empty array shows the empty illustration.
   * Keep the array reference stable, or each new array replaces local edits.
   */
  devices?: AccountDevice[];
  /** Plan cap. Defaults to the sample plan limit of 3. */
  deviceLimit?: number;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`dv-skel ${className}`} />;
}

function roleLabel(device: AccountDevice): string {
  const word = device.primary ? 'Primary' : 'Secondary';
  const who = `${device.name}, ${device.detail}${device.current ? ', this device' : ''}`;
  return device.primary ? `${word}, ${who}` : `${word}, set ${who} as primary`;
}

function menuLabel(device: AccountDevice): string {
  const bits = [device.name];
  if (device.current) bits.push('this device');
  if (device.primary) bits.push('primary');
  return `Actions for ${bits.join(', ')}`;
}

function LimitSkeleton() {
  return (
    <div className="dv-limit" data-testid="skeleton-limit" aria-hidden="true">
      <div className="dv-limit__top">
        <Skel className="dv-skel--badge" />
        <div className="dv-skel__stack">
          <Skel className="dv-skel--title" />
          <Skel className="dv-skel--line" />
        </div>
      </div>
      <Skel className="dv-skel--bar" />
      <Skel className="dv-skel--line" />
    </div>
  );
}

function PlanSkeleton() {
  return (
    <div className="dv-plan" data-testid="skeleton-plan" aria-hidden="true">
      <Skel className="dv-skel--ico" />
      <div className="dv-skel__stack">
        <Skel className="dv-skel--title" />
        <Skel className="dv-skel--line" />
        <Skel className="dv-skel--short" />
      </div>
    </div>
  );
}

function DevicesSkeleton() {
  return (
    <div data-testid="skeleton-devices" aria-hidden="true">
      <div className="dv-panel__bar">
        <Skel className="dv-skel--title" />
        <Skel className="dv-skel--sort" />
      </div>
      <Skel className="dv-skel--head" />
      <div className="dv-skel__rows">
        {[0, 1, 2].map((key) => (
          <Skel key={key} className="dv-skel--row" />
        ))}
      </div>
    </div>
  );
}

function PrimarySkeleton() {
  return (
    <div className="dv-note" data-testid="skeleton-primary" aria-hidden="true">
      <Skel className="dv-skel--ico" />
      <div className="dv-skel__stack">
        <Skel className="dv-skel--short" />
        <Skel className="dv-skel--line" />
        <Skel className="dv-skel--line" />
      </div>
    </div>
  );
}

function DeviceIcon({ device }: { device: AccountDevice }) {
  const Icon = device.kind === 'laptop' ? Laptop : Smartphone;
  return (
    <span className={`dv-ico${device.primary ? ' is-primary' : ''}`}>
      <Icon size={18} aria-hidden="true" />
      {device.primary && (
        <span className="dv-ico__crown" aria-hidden="true">
          <Crown size={10} />
        </span>
      )}
    </span>
  );
}

const DevicesView: React.FC<DevicesViewProps> = ({
  loading: loadingProp,
  devices: devicesProp,
  deviceLimit,
}) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const limit = safeDeviceLimit(deviceLimit ?? PLAN_DEVICE_LIMIT);
  const sortId = useId();
  const noteTitleId = useId();
  const dialogTitleId = useId();
  const dialogBodyId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<DeviceSort>('recent');
  const [rows, setRows] = useState<AccountDevice[]>(() => cloneDevices(devicesProp ?? SAMPLE_DEVICES));
  const [menuId, setMenuId] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setRows(cloneDevices(devicesProp ?? SAMPLE_DEVICES));
    setMenuId(null);
    setPendingId(null);
  }, [devicesProp]);

  useEffect(() => {
    if (!menuId) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) setMenuId(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuId(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);

  const pending = rows.find((device) => device.id === pendingId) ?? null;

  useEffect(() => {
    if (!pending) return undefined;
    const node = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const items = () =>
      Array.from(
        node?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea') ?? []
      ).filter((item) => !item.hasAttribute('disabled'));
    items()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPendingId(null);
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const focusable = items();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [pending]);

  const visible = sortDevices(rows, sort);
  const count = rows.length;
  const percent = usagePercent(count, limit);
  const primary = rows.find((device) => device.primary) ?? null;
  const canRestore = devicesProp === undefined;

  const makePrimary = (device: AccountDevice) => {
    if (device.primary) return;
    setRows((current) => setPrimaryDevice(current, device.id));
    setMenuId(null);
    setNotice(`${device.name} is now the primary device.`);
  };

  const confirmRemove = () => {
    if (!pending) return;
    const name = pending.name;
    setRows((current) => removeDevice(current, pending.id));
    setPendingId(null);
    setMenuId(null);
    setNotice(`${name} was removed.`);
  };

  const restoreSample = () => {
    setRows(cloneDevices(SAMPLE_DEVICES));
    setSort('recent');
    setNotice('Sample devices restored.');
  };

  return (
    <div className="dv" data-testid="devices-screen" aria-busy={loading}>
      <p className="visually-hidden" aria-live="polite">
        {notice}
      </p>

      {loading ? (
        <>
          <p className="visually-hidden" role="status">
            Loading devices
          </p>
          <section className="dv-summary">
            <LimitSkeleton />
            <PlanSkeleton />
          </section>
          <section className="dv-panel">
            <DevicesSkeleton />
            <PrimarySkeleton />
          </section>
        </>
      ) : (
        <>
          <section className="dv-summary" aria-label="Device limit">
            <div className="dv-limit">
              <div className="dv-limit__top">
                <span className="dv-badge" aria-hidden="true">
                  <ShieldCheck size={22} />
                </span>
                <div>
                  <h2 className="dv-limit__title">{`Device limit: ${limit}`}</h2>
                  <p className="dv-limit__count">{usageText(count, limit)}</p>
                </div>
              </div>
              <div className="dv-meter">
                <div
                  className="dv-meter__track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(limit, count)}
                  aria-valuenow={count}
                  aria-valuetext={usageText(count, limit)}
                  aria-label="Devices in use"
                >
                  {percent > 0 && <span className="dv-meter__fill" style={{ width: `${percent}%` }} />}
                </div>
                <span className="dv-meter__count">{`${count}/${limit}`}</span>
              </div>
              <p className="dv-limit__hint">{usageHint(count, limit)}</p>
            </div>
            <div className="dv-plan">
              <span className="dv-info" aria-hidden="true">
                <Info size={18} />
              </span>
              <div>
                <h2 className="dv-plan__title">Device limit set by your plan</h2>
                <p>{planText(limit)}</p>
              </div>
            </div>
          </section>

          {count === 0 ? (
            <section className="dv-panel dv-empty" data-testid="devices-empty">
              <DevicesArt />
              <h2>No devices signed in</h2>
              <p>Phones and browsers that sign in to this MockLoc account will show up here.</p>
              {canRestore && (
                <button type="button" className="dv-restore" onClick={restoreSample}>
                  Show sample devices
                </button>
              )}
            </section>
          ) : (
            <section className="dv-panel" aria-labelledby="devices-heading">
              <div className="dv-panel__bar">
                <h2 id="devices-heading" className="dv-panel__title">{`Your devices (${count})`}</h2>
                <div className="dv-sort">
                  <label htmlFor={sortId}>Sort by:</label>
                  <span className="dv-sort__control">
                    <select
                      id={sortId}
                      value={sort}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (!isDeviceSort(next)) return;
                        setSort(next);
                        setMenuId(null);
                      }}
                    >
                      <option value="recent">Recently active</option>
                      <option value="name">Name</option>
                      <option value="status">Status</option>
                    </select>
                    <ChevronDown size={16} aria-hidden="true" />
                  </span>
                </div>
              </div>

              <div className="dv-cols" aria-hidden="true">
                <span>Device</span>
                <span>Status</span>
                <span>Last activity</span>
                <span>Actions</span>
              </div>

              <ul className="dv-list">
                {visible.map((device) => {
                  const open = menuId === device.id;
                  return (
                    <li
                      key={device.id}
                      className={`dv-device${device.primary ? ' is-primary' : ''}${open ? ' is-open' : ''}`}
                    >
                      <div className="dv-id">
                        <DeviceIcon device={device} />
                        <span className="dv-id__copy">
                          <span className="dv-id__name">
                            <strong>{device.name}</strong>
                            {device.current && <span className="dv-chip">This device</span>}
                          </span>
                          <small>{device.detail}</small>
                        </span>
                      </div>
                      <p className={`dv-status${device.presence === 'active' ? ' is-live' : ''}`}>
                        <span className="dv-dot" aria-hidden="true" />
                        {device.presenceLabel}
                      </p>
                      <p className="dv-when">
                        <strong>{device.lastActiveLabel}</strong>
                        {device.agoLabel ? <small>{device.agoLabel}</small> : null}
                      </p>
                      <div className="dv-actions">
                        <button
                          type="button"
                          className={`dv-role${device.primary ? ' dv-role--primary' : ' dv-role--secondary'}`}
                          aria-pressed={device.primary}
                          disabled={device.primary}
                          aria-label={roleLabel(device)}
                          onClick={() => makePrimary(device)}
                        >
                          {device.primary ? 'Primary' : 'Secondary'}
                        </button>
                        <div className="dv-morewrap" ref={open ? menuRef : undefined}>
                          <button
                            type="button"
                            className="dv-more"
                            aria-label={menuLabel(device)}
                            aria-haspopup="menu"
                            aria-expanded={open}
                            onClick={() => setMenuId((current) => (current === device.id ? null : device.id))}
                          >
                            <MoreVertical size={18} aria-hidden="true" />
                          </button>
                          {open && (
                            <div className="dv-menu" role="menu" aria-label={menuLabel(device)}>
                              {!device.primary && (
                                <button type="button" role="menuitem" onClick={() => makePrimary(device)}>
                                  Set as primary
                                </button>
                              )}
                              <button
                                type="button"
                                role="menuitem"
                                className="dv-menu__danger"
                                onClick={() => {
                                  setMenuId(null);
                                  setPendingId(device.id);
                                }}
                              >
                                Remove device
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {primary && (
                <aside className="dv-note" aria-labelledby={noteTitleId}>
                  <span className="dv-note__icon" aria-hidden="true">
                    <Crown size={18} />
                  </span>
                  <div>
                    <h2 id={noteTitleId} className="dv-note__title">Primary device</h2>
                    <p>{PRIMARY_NOTE}</p>
                  </div>
                </aside>
              )}
            </section>
          )}
        </>
      )}

      {pending && (
        <div
          className="dv-modal"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPendingId(null);
          }}
        >
          <div
            className="dv-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            aria-describedby={dialogBodyId}
            ref={dialogRef}
          >
            <h2 id={dialogTitleId}>Remove device?</h2>
            <p id={dialogBodyId}>{removeMessage(pending, rows.length - 1)}</p>
            <div className="dv-dialog__actions">
              <button type="button" className="dv-ghost" onClick={() => setPendingId(null)}>
                Cancel
              </button>
              <button type="button" className="dv-danger" onClick={confirmRemove}>
                Remove device
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesView;
